import { useState, useEffect } from 'react';
import { Service } from '@/types/service';
import { ServiceCard } from './ServiceCard';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';

interface ServiceMonitorProps {
  services: Service[];
  onServicesUpdate: (services: Service[]) => void;
  onRefresh: () => void;
  isChecking: boolean;
}

export const ServiceMonitor = ({ services, onServicesUpdate, onRefresh, isChecking }: ServiceMonitorProps) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const categories = ['all', ...Array.from(new Set(services.map(s => s.category)))];

  // Helper function to sort services by status priority
  const sortServicesByStatus = (servicesList: Service[]) => {
    const statusPriority: Record<string, number> = {
      'down': 0,
      'degraded': 1,
      'unknown': 2,
      'healthy': 3,
    };
    
    return [...servicesList].sort((a, b) => {
      const priorityA = statusPriority[a.status] ?? 4;
      const priorityB = statusPriority[b.status] ?? 4;
      
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      
      // Secondary sort by name for same status
      return a.name.localeCompare(b.name);
    });
  };

  const filteredServices = selectedCategory === 'all'
    ? services
    : services.filter(s => s.category === selectedCategory);

  // Separate services with and without status pages
  const servicesWithStatus = sortServicesByStatus(
    filteredServices.filter(s => s.url && s.url.trim() !== '')
  );
  const servicesWithoutStatus = sortServicesByStatus(
    filteredServices.filter(s => !s.url || s.url.trim() === '')
  );

  // Auto-refresh every 5 minutes (optimized to reduce server load)
  useEffect(() => {
    if (services.length > 0) {
      // Initial check on mount
      const timer = setTimeout(() => {
        onRefresh();
      }, 2000); // Small delay to avoid double-checking on initial load
      
      // Set up 5-minute interval
      const interval = setInterval(onRefresh, 300000); // 5 minutes
      
      return () => {
        clearTimeout(timer);
        clearInterval(interval);
      };
    }
  }, [services.length]);

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div className="flex gap-2 flex-wrap">
          {categories.map(category => (
            <Button
              key={category}
              variant={selectedCategory === category ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(category)}
              className="capitalize"
            >
              {category}
            </Button>
          ))}
        </div>

        <Button
          onClick={onRefresh}
          disabled={isChecking}
          variant="outline"
          size="sm"
          className="gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${isChecking ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {servicesWithStatus.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-border to-transparent" />
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide px-2">
              Services with Status Pages ({servicesWithStatus.length})
            </h2>
            <div className="h-px flex-1 bg-gradient-to-r from-border via-border to-transparent" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {servicesWithStatus.map(service => (
              <ServiceCard
                key={service.id}
                service={service}
                onClick={() => {
                  if (service.url) {
                    window.open(service.url, '_blank', 'noopener,noreferrer');
                  }
                }}
              />
            ))}
          </div>
        </div>
      )}

      {servicesWithoutStatus.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-border to-transparent" />
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide px-2">
              Services without Status Pages ({servicesWithoutStatus.length})
            </h2>
            <div className="h-px flex-1 bg-gradient-to-r from-border via-border to-transparent" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {servicesWithoutStatus.map(service => (
              <ServiceCard
                key={service.id}
                service={service}
                onClick={() => {
                  if (service.url) {
                    window.open(service.url, '_blank', 'noopener,noreferrer');
                  }
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
